<template>
  <div v-if="currentProject">
    <CommonHeader :title="currentProject.title" :icon="projectIcon">
      <div slot="submenu-left">
        <button
          v-shortkey="{ ixwin: ['ctrl', 'z'], mac: ['meta', 'z'] }"
          @shortkey="performUndo()"
          @click="performUndo()"
          :disabled="!undoable"
        >
          <b-icon icon="arrow-counterclockwise" />
        </button>
        <button
          v-shortkey="{ ix: ['shift', 'ctrl', 'z'], win: ['ctrl', 'y'], mac: ['shift', 'meta', 'y'] }"
          @shortkey="performRedo()"
          @click="performRedo()"
          :disabled="!redoable"
        >
          <b-icon icon="arrow-clockwise" />
        </button>

        <toolbar-item
          v-if="!isProjectPlanPrint"
          icon="printer-fill"
          :to="{ name: 'ProjectPlanPrint', params: { _id: $route.params._id }, query }"
        />

        <toolbar-item
          v-if="isProjectPlanPrint"
          icon="bar-chart-steps"
          :to="{ name: 'ProjectPlanEdit', params: { _id: $route.params._id }, query }"
        />

        <toolbar-item
          v-if="hasSimplePermissions('viewProjectOverview') || true"
          icon="clipboard-data"
          :to="{ name: 'EditProject', params: { _id: $route.params._id } }"
        />

        <toolbar-item icon="zoom-out" @click="changeZoom(-1)" />
        <toolbar-item icon="zoom-in" @click="changeZoom(+1)" />

        <toolbar-item icon="view-list" @click="changeLineHeight(-3)" />
        <toolbar-item icon="view-stacked" @click="changeLineHeight(+3)" />

        <toolbar-item icon="gear-fill">
          <b-labelled
            v-close-popover
            icon="arrow-left-square-fill"
            :title="$t('Reset filters')"
            @click.native="resetFilters"
          />

          <hr />

          <b-labelled
            v-if="lockedForEditing && hasSimplePermissions('lockProject')"
            v-close-popover
            icon="unlock-fill"
            :title="$t('Unlock Project')"
            :disabled="!unlockableByCurrentUser"
            @click.native="lockedForEditing = false"
          />

          <b-labelled
            v-if="lockedForEditing && !lockedForEditingByCurrentUser"
            v-close-popover
            icon="lock-fill"
            :title="$t('Locked by {username}', { username: currentProject.lockedForEditingBy })"
          >
            <span style="font-size: 50%">({{ $t(locktime) }})</span>
          </b-labelled>

          <b-labelled
            v-if="!lockedForEditing && hasSimplePermissions('lockProject')"
            v-close-popover
            icon="lock-fill"
            :title="$t('Lock Project')"
            @click.native="lockedForEditing = true"
          />

          <b-labelled
            v-if="lockedDragNDrop && hasSimplePermissions('saveProjectProperties')"
            v-close-popover
            icon="slash-circle"
            :title="$t('Unlock Dragging')"
            :disabled="!editingAllowed"
            @click.native="lockedDragNDrop = false"
          />

          <b-labelled
            v-if="!lockedDragNDrop && hasSimplePermissions('saveProjectProperties')"
            v-close-popover
            icon="arrow-left-right"
            :title="$t('Lock Dragging')"
            :disabled="!editingAllowed"
            @click.native="lockedDragNDrop = true"
          />

          <hr />

          <b-labelled
            v-close-popover
            icon="exclamation-triangle-fill"
            :title="$t('Toggle Alerts')"
            @click.native="
              showAlerts = !showAlerts;
              updateUrl();
            "
          />

          <b-labelled
            v-if="filterManagerId !== loggedInPerson._id"
            v-close-popover
            icon="person-fill"
            :title="$t('Show My Stages')"
            @click.native="updateMentionFilter(loggedInPerson._id)"
          />

          <b-labelled
            v-if="filterManagerId !== ''"
            v-close-popover
            icon="person-fill"
            :title="$t('Show All Stages')"
            @click.native="updateMentionFilter('')"
          />

          <b-labelled
            v-if="!filterDeliveries"
            v-close-popover
            icon="truck"
            :title="$t('Show Only Deliveries')"
            @click.native="
              filterDeliveries = true;
              updateUrl();
            "
          />

          <b-labelled
            v-if="filterDeliveries"
            v-close-popover
            icon="truck"
            :title="$t('Show Regardless Delivery')"
            @click.native="
              filterDeliveries = false;
              updateUrl();
            "
          />
        </toolbar-item>

        <toolbar-item icon="calendar3-week-fill" :title="`${filterDate} - ${filterDateEnd}`">
          <div class="project-date-range-datepickers">
            <DatePicker v-model="filterDate" @input="updateUrl()"></DatePicker>
            <DatePicker v-model="filterDateEnd" @input="updateUrl()"></DatePicker>
          </div>
        </toolbar-item>
      </div>
      <div slot="submenu-center" class="d-flex align-items-center">
        <At
          :tabSelect="true"
          :value="filterText"
          @input="updateFilterText"
          :members="projectPersons"
          nameKey="firstName"
        >
          <span slot="embeddedItem" slot-scope="item">
            <span :data-id="item.current._id" class="tag"
              >{{ item.current.firstName }} {{ item.current.lastName }}</span
            >
          </span>
          <template slot="item" slot-scope="row">
            <span>{{ row.item.firstName }} {{ row.item.lastName }}</span>
          </template>
          <div @blur="setFilterText()" class="hidden-on-small-screen filterText" contenteditable />
        </At>
        <b-icon
          v-tooltip="{
            content: $t('search-instructions'),
            placement: 'right',
            offset: 20,
            delay: {
              show: 200,
              hide: 200,
            },
            classes: ['tooltip-bubble'],
          }"
          icon="info-circle-fill"
          class="ml-1 cursor-help"
        />
      </div>
    </CommonHeader>
    <main>
      <router-view
        :zoom="zoom"
        :lineheight="lineheight"
        :filterText="filterTextSanitized"
        :filterDate="filterDate"
        :filterDateEnd="filterDateEnd"
        :filterManagerId="filterManagerId"
        :filterDeliveries="filterDeliveries"
        :showAlerts="showAlerts"
      />
    </main>
    <CommonFooter />
  </div>
</template>
<script>
import { EventBus } from '@/common/eventbus';
import { fuzzytime } from '@/common/timeHelpers';
import { mapActions, mapGetters } from 'vuex';
import At from 'vue-at/dist/vue-at';
import BLabelled from '@/components/toolbar/BLabelled';
import CommonFooter from '@/components/toolbar/CommonFooter';
import CommonHeader from '@/components/toolbar/CommonHeader';
import DatePicker from '@/components/datepicker/DatePicker';
import debounce from 'debounce';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import ToolbarItem from '@/components/toolbar/ToolbarItem';
import { extractMentions, setMention } from '@/common/filters';

export default {
  name: 'ProjectPlan',
  data() {
    return {
      filterDate: this.currentProject ? this.currentProject.startdate : moment().startOf('year').format('YYYY-MM-DD'),
      filterDateEnd: this.currentProject ? this.currentProject.enddate : moment().endOf('year').format('YYYY-MM-DD'),
      filterDeliveries: false,
      filterManagerId: '',
      filterText: '',
      filterTextSanitized: '',
      showAlerts: false,
      undoing: false,
      zoom: 1,
      lineheight: 20,
    };
  },
  created: async function () {
    // this.selectPart(null); - dont because then selecting in actionplan does not have effect
    console.debug('ProjectPlan:mounted', 'query', JSON.stringify(this.$route.query));

    await this.loadProject(this.$route.params._id);
    this.performQuery();
    console.debug('ProjectPlan:mounted:applied filters', 'query', JSON.stringify(this.$route.query));

    this.updateZoomFactor();
    console.debug('ProjectPlan:mounted:zoomed', 'query', JSON.stringify(this.$route.query));

    if (this.lockedForEditing && !this.lockedForEditingByCurrentUser) {
      EventBus.$emit('showMessageToUser', {
        message: this.$t('This project is locked for editing by {username}. {lockedat}', {
          username: this.currentProject.lockedForEditingBy,
          lockedat: this.$t(fuzzytime(this.currentProject.lockedForEditingAt)),
        }),
        type: 'error',
        duration: 15000,
      });
    }

    this.$nextTick(() => {
      window.addEventListener('resize', this.updateZoomFactor);
      // this.updateZoomFactor();
      console.debug('ProjectPlan:mounted:nextTick', 'query', JSON.stringify(this.$route.query));
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateZoomFactor);
    EventBus.$off('openPartContextMenu');
    EventBus.$off('openStageContextMenu');
  },
  methods: {
    ...mapActions([
      'loadProject',
      'lockProject',
      'redo',
      'saveProjectProperties',
      'selectPart',
      'selectStage',
      'setViewSetting',
      'undo',
    ]),

    changeZoom(diff) {
      this.zoom = Math.min(10, Math.max(1, this.zoom + diff));
      this.updateUrl();
    },

    changeLineHeight(diff) {
      this.lineheight = Math.min(32, Math.max(14, this.lineheight + diff));
      this.updateUrl();
    },

    performRedo: async function () {
      if (!this.undoing) {
        this.undoing = true;
        await this.redo();
        this.undoing = false;
      }
    },
    performUndo: async function () {
      if (!this.undoing) {
        this.undoing = true;
        await this.undo();
        this.undoing = false;
      }
    },
    updateMentionFilter: function (id) {
      this.filterManagerId = id;
      this.filterText = setMention(this.filterText, id ? this.getPerson(id) : '');
      this.filterTextSanitized = this.sanitize(this.filterText);
      this.updateUrl();
    },
    setFilterText: function () {
      this.filterText = setMention(
        this.filterTextSanitized,
        this.filterManagerId ? this.getPerson(this.filterManagerId) : ''
      );
    },
    updateFilterText: debounce(function (e) {
      let { text, mentions } = extractMentions(this.sanitize(e));

      this.filterTextSanitized = text;
      if (mentions.length > 0) {
        this.filterManagerId = mentions[0];
      } else {
        this.filterManagerId = '';
      }
      this.updateUrl();
    }, 400),
    resetFilters: function () {
      this.filterDate = this.currentProject.projectStartdate;
      this.filterDateEnd = this.currentProject.projectEnddate;
      this.filterText = '';
      this.filterTextSanitized = '';
      this.filterManagerId = '';
      this.showAlerts = false;
      this.filterDeliveries = false;
      this.zoom = 1;
      this.lineheight = 20;
      this.updateUrl();
    },

    updateZoomFactor: function () {
      var newZoomFactor = Math.round(((window.innerWidth - 210) / this.shownWeeks / 7) * 10) / 10;
      // var newZoomFactor = Math.round(10 * ((this.$refs.outerParts ? this.$refs.outerParts.offsetWidth : 1500) - 210) / this.shownWeeks / 7) / 10;
      console.log('newZoomFactor', newZoomFactor, window.innerWidth, this.shownWeeks);
      if (newZoomFactor !== this.viewSettings.projectPlan.zoomFactor) {
        this.$nextTick(function () {
          this.setViewSetting({
            section: 'projectPlan',
            property: 'zoomFactor',
            newValue: newZoomFactor,
          });
        });
      }
    },
    updateUrl: function () {
      if (JSON.stringify(this.query) !== JSON.stringify(this.$router.currentRoute.query)) {
        this.$router.push({ query: this.query });
      }
    },
    performQuery: function () {
      this.filterDate = this.$route.query.from ? this.$route.query.from : this.currentProject.projectStartdate;
      this.filterDateEnd = this.$route.query.to ? this.$route.query.to : this.currentProject.projectEnddate;
      if (this.filterDateEnd <= this.filterDate) {
        this.filterDate = this.currentProject.projectStartdate;
        this.filterDateEnd = this.currentProject.projectEnddate;
      }
      this.zoom = this.$route.query.zoom ? parseInt(this.$route.query.zoom) : 1;
      this.lineheight = this.$route.query.lineheight ? parseInt(this.$route.query.lineheight) : 20;
      this.filterManagerId = this.$route.query.filterManagerId;
      if (this.$route.query.filterText) {
        if (this.filterTextSanitized !== this.$route.query.filterText) {
          this.filterText = this.$route.query.filterText;
          this.filterTextSanitized = this.sanitize(this.filterText);
        }
        // this.filterText = this.$route.query.filterText ? this.$route.query.filterText : '';
      }
      this.filterDeliveries = this.$route.query.hasOwnProperty('filterDeliveries');
      this.showAlerts = this.$route.query.hasOwnProperty('showAlerts');
    },
    sanitize: function (text) {
      return sanitizeHtml(text, {
        allowedTags: ['span'],
        allowedAttributes: {
          span: ['data-id', 'contenteditable'],
        },
        allowedClasses: {
          span: ['tag'],
        },
      });
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'editingAllowed',
      'getPerson',
      'hasPropertyPermissions',
      'hasSimplePermissions',
      'lockedForEditingByCurrentUser',
      'loggedInPerson',
      'projectPersons',
      'redoable',
      'undoable',
      'username',
      'viewSettings',
    ]),

    isProjectPlanPrint() {
      return this.$route.name === 'ProjectPlanPrint';
    },

    shownWeeks: function () {
      return -moment(this.filterDate).diff(this.filterDateEnd, 'weeks') + 1;
    },
    projectIcon: function () {
      if (this.lockedForEditing && this.lockedForEditingByCurrentUser) {
        return 'file-lock2';
      }
      if (this.lockedForEditing && !this.lockedForEditingByCurrentUser) {
        return 'lock';
      }
      return '';
    },
    locktime: function () {
      return fuzzytime(this.currentProject.lockedForEditingAt);
    },
    lockedDragNDrop: {
      get: function () {
        return this.currentProject.lockedDragNDrop;
      },
      set: function (value) {
        this.saveProjectProperties({
          updateProject: this.currentProject,
          newProjectProperties: { lockedDragNDrop: value },
        });
      },
    },
    lockedForEditing: {
      get: function () {
        return this.currentProject.lockedForEditingBy;
      },
      set: function (value) {
        this.lockProject(value);
      },
    },
    unlockableByCurrentUser: function () {
      var lockedAt = this.currentProject.lockedForEditingAt ? this.currentProject.lockedForEditingAt : '1900-01-01';

      return this.currentProject.lockedForEditingBy === this.username || moment().diff(lockedAt, 'minute') > 60;
    },
    query: function () {
      var query = {};
      if (this.lineheight !== 20) {
        query.lineheight = this.lineheight;
      }
      if (this.zoom > 1) {
        query.zoom = this.zoom;
      }
      if (this.filterDate !== this.currentProject.projectStartdate) {
        query.from = this.filterDate;
      }
      if (this.filterDateEnd !== this.currentProject.projectEnddate) {
        query.to = this.filterDateEnd;
      }
      if (this.filterDeliveries) {
        query.filterDeliveries = true;
      }
      if (this.showAlerts) {
        query.showAlerts = true;
      }
      if (this.filterText !== '') {
        query.filterText = this.filterTextSanitized;
      }
      if (this.filterManagerId !== '') {
        query.filterManagerId = this.filterManagerId;
      }
      return query;
    },
  },
  components: {
    At,
    BLabelled,
    CommonFooter,
    CommonHeader,
    DatePicker,
    ToolbarItem,
  },
  watch: {
    $route: async function (to, from) {
      await this.loadProject(to.params._id);
      this.performQuery();
      if (Object.keys(to.query).length === 0) {
        this.resetFilters();
      }
    },
  },
};
</script>

<style>
/* This one cant be scoped, because its added dynamically */
.filterText .tag {
  border: 1px outset gray;
  background: beige;
  border-radius: 4px;
  padding: 1px 2px;
  font-size: 90%;
  color: black;
}
</style>

<style scoped>
/** @todo This should not be scoped. */
div {
  font-weight: normal;
}

h1 {
  font-size: 120%;
  text-align: left;
  margin: 0px;
}

.filterText:focus {
  outline-style: none;
}

.filterText {
  border: none;
  border-radius: 20px;
  padding: 5px 10px;
  width: 20em;
  white-space: nowrap;
  overflow: hidden;
  font-size: 80%;
  max-height: 2em;
  background-color: #ffffff;
  color: #212529;
}

.vertical-align-middle {
  display: flex;
}

.title {
  width: 160px;
  text-overflow: ellipsis;
  z-index: 0;
}

.duration {
  width: 40px;
  text-align: right;
  padding-right: 1em;
}
.startdate {
  width: 70px;
  text-align: right;
}

.editChild {
  background-color: white;
  width: 280px;
  right: 0px;
  position: fixed;
  bottom: 0px;
  top: 120px;
  z-index: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  border-left: 2px solid black;
  color: black;
  padding: 2px 10px;
}

.sortable-ghost {
  border: 2px dotted grey;
}

.titles {
  white-space: nowrap;
  font-size: 10px;
  background: white;
  position: fixed;
  width: 100%;
  z-index: 0;
  color: black;
}

.monthTitle {
  display: block;
  position: absolute;
  padding-left: 2px;
  border-left: 1px solid #aaaaaa;
  height: 15px;
  text-overflow: hidden;
}

.weekTitle {
  display: block;
  position: absolute;
  padding-left: 2px;
  border-left: 1px solid #aaaaaa;
  height: 2500px;
  text-overflow: hidden;
}

.weekTitle:hover {
  background: #eee;
}

.dayTitle {
  display: block;
  position: absolute;
  border-bottom: 1px solid #bbb;
  width: 2500px;
  transform-origin: 0% 0%;
  transform: rotate(90deg) translateY(-100%);
  text-overflow: hidden;
  overflow: hidden;
  font-size: 10px;
  vertical-align: bottom;
}

.dayTitle:hover {
  background: #eee;
}

.dayHighLightWeekEnd {
  background: #fde5c5;
}

.partTitles .child {
  border: none;
}
.partTitles .child.selected {
  background: transparent;
}

.dragSpace {
  height: 5px;
}

.activePeriod {
  background: #eee;
}

.activeDay {
  background: #ddd;
}

button:disabled {
  color: grey;
}

.project-date-range-datepickers {
  display: flex;
  flex-direction: row;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/ProjectPlan.vue